import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CopyIcon, CheckIcon, ExclamationTriangleIcon, DownloadIcon } from '@radix-ui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw'; // Add support for raw HTML
import { useDispatch } from 'react-redux';
import { openImage, setIsTranslating } from '../../../store/chatSlice';
import { useMsal } from '@azure/msal-react';
import { downloadAnalysisFile } from '../../../utils/document/fileDownload.service';
import rehypeAnalysisfile from '../../../utils/rehypeAnalysisfile';
import { FilePreview } from '../input/upload/FilePreview';
import JSZip from 'jszip';
import 'katex/dist/katex.min.css';
import { useTheme } from '../../Theme/ThemeProvider';
import logger from "../../../utils/logger/logger";
import { ChatContentItem } from '../../../utils/chat/chat.service';
import { formatFileSize } from '../translation/TranslationMode';
import { useDeviceDetection } from '../../../hooks/useDeviceDetection';
import ClickableImageThumbnail from '../../common/ClickableImageThumbnail';
import { PreviewableFileAttachment } from './PreviewableFileAttachment';

// Global tracking of auto-downloaded translation files (across all message instances)
const globalAutoDownloadedFiles = new Set<string>();

// Export function to clear the auto-downloaded files set (called when navigating home)
export const clearAutoDownloadedFiles = () => {
  globalAutoDownloadedFiles.clear();
};

interface TranslationFileData {
  filename: string;
  content_base64: string;
  language: string;
}

const MIME_BY_EXTENSION: Record<string, string> = {
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  htm: 'text/html',
  html: 'text/html',
  pdf: 'application/pdf',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
};

function inferMimeTypeFromFilename(filename: string) {
  const extension = filename.split('.').pop()?.toLowerCase() || '';
  return MIME_BY_EXTENSION[extension] || 'application/octet-stream';
}

// Content reaches rehype-raw with allowDangerousHtml, so this must run to a fixpoint:
// a single pass lets nesting like `<thi<thinking></thinking>nking>` re-form a live tag.
// Callers must decode HTML entities BEFORE calling this, or `&lt;thinking&gt;` slips through.
function stripThinkingTags(text: string) {
  let current = text;
  let previous: string;
  do {
    previous = current;
    current = current
      .replace(/<thinking>[\s\S]*?<\/thinking>/gi, '')
      .replace(/<\/?thinking[^>]*>/gi, '');
  } while (current !== previous);
  return current;
}

// `$` that clearly starts a currency amount (optional space then a digit) and is neither
// part of `$$` display math nor already escaped.
const CURRENCY_DOLLAR_PATTERN = /(^|[^\\$])\$(?=\s?\d)/g;
// Regions whose `$` must stay literal: fenced/inline code, markdown link destinations,
// autolinks and bare URLs (e.g. https://host/task/2998$46522.htmld).
// Code spans use a backreferenced delimiter run so ``a $5 b`` is one region, not two empty
// ones; link destinations and bare URLs allow balanced parens as CommonMark does.
const DOLLAR_SAFE_REGION_PATTERN = new RegExp(
  [
    '(?:^|\\n)[ \\t]*(?:```|~~~)[\\s\\S]*?(?:\\n[ \\t]*(?:```|~~~)|$)',
    '(`+)[\\s\\S]*?\\1(?!`)',
    '\\]\\((?:[^()\\s]|\\((?:[^()]|\\([^()]*\\))*\\))*(?:\\s+"[^"]*")?\\)',
    '<[a-z][a-z0-9+.-]*:[^>\\s]*>',
    '(?:https?|ftp):\\/\\/(?:[^\\s<>\\[\\]()]|\\((?:[^\\s()]|\\([^\\s()]*\\))*\\))+',
  ].join('|'),
  'gi',
);

/**
 * Escape currency `$` so remark-math (singleDollarTextMath) does not treat text like
 * `$5 and $10` as inline math, while leaving URLs and code untouched.
 */
const escapeCurrencyDollars = (text: string): string => {
  let result = '';
  let lastIndex = 0;
  DOLLAR_SAFE_REGION_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = DOLLAR_SAFE_REGION_PATTERN.exec(text)) !== null) {
    result += text.slice(lastIndex, match.index).replace(CURRENCY_DOLLAR_PATTERN, '$1\\$');
    result += match[0];
    lastIndex = match.index + match[0].length;
  }
  return result + text.slice(lastIndex).replace(CURRENCY_DOLLAR_PATTERN, '$1\\$');
};

interface MessageContentRendererProps {
  content: string | ChatContentItem[];
  className?: string;
  isStreaming?: boolean;
  debugInfo?: any;
  disableNormalization?: boolean; // when true, render raw content (used for reasoning dropdown)
  onContentLoaded?: () => void; // Called when all images/PDFs finish loading
  translationData?: TranslationFileData[]; // Translation files for download buttons
  /** The ModelConfig.id of the model that generated this message (e.g. 'gpt-image-2'). Used for per-model UI behaviour. */
  modelId?: string | null;
}

export const MessageContentRenderer: React.FC<MessageContentRendererProps> = React.memo(({ content, className = '', isStreaming = false, disableNormalization = false, onContentLoaded, translationData, modelId }) => {
  const dispatch = useDispatch();
  const { mode } = useTheme();
  const { isMobile } = useDeviceDetection();
  const [imageError, setImageError] = React.useState(false);
  // Global download error popup trigger
  const [downloadErrorGlobal, setDownloadErrorGlobal] = React.useState(false);

  useEffect(() => {
    if (translationData && translationData?.length > 0) {
      dispatch(setIsTranslating(false)); // Reset translating state when content changes (e.g., new message)
    }
  }, [dispatch, translationData])
  
  const reportDownloadError = React.useCallback(() => {
    setDownloadErrorGlobal(true);
  }, []);
  
  // Handler for downloading translation files
  const handleTranslationDownload = React.useCallback((filename: string, content_base64: string) => {
    try {
      // Convert base64 to blob
      const byteCharacters = atob(content_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Infer MIME type from filename
      const mimeType = inferMimeTypeFromFilename(filename);
      const blob = new Blob([byteArray], { type: mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('[MessageContentRenderer] Failed to download translation file', { filename, error });
      reportDownloadError();
    }
  }, [reportDownloadError]);
  
  // Auto-dismiss popup after 4s
  useEffect(() => {
    if (downloadErrorGlobal) {
      const t = setTimeout(() => setDownloadErrorGlobal(false), 4000);
      return () => clearTimeout(t);
    }
  }, [downloadErrorGlobal]);
  
  // Helpers for download-all
  const base64ToUint8Array = (b64: string) => {
    const cleaned = b64.replace(/\s+/g, '');
    const lookup = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    const padding = (cleaned.endsWith('==') ? 2 : cleaned.endsWith('=') ? 1 : 0);
    const len = Math.floor((cleaned.length * 3) / 4) - padding;
    const u8 = new Uint8Array(len);
    let p = 0;
    for (let i = 0; i < cleaned.length; i += 4) {
      const chunk = cleaned.substr(i, 4);
      const vals = [lookup.indexOf(chunk[0]), lookup.indexOf(chunk[1]), lookup.indexOf(chunk[2]), lookup.indexOf(chunk[3])];
      const bits = (vals[0] << 18) | (vals[1] << 12) | ((vals[2] & 63) << 6) | (vals[3] & 63);
      if (p < len) u8[p++] = (bits >> 16) & 0xFF;
      if (p < len) u8[p++] = (bits >> 8) & 0xFF;
      if (p < len) u8[p++] = bits & 0xFF;
    }
    return u8;
  };

  const downloadAll = React.useCallback(async (files: TranslationFileData[]) => {
    try {
      const zip = new JSZip();
      files.forEach(f => {
        const data = base64ToUint8Array(f.content_base64.replace(/^data:[^;]+;base64,/, ''));
        zip.file(f.filename, data);
      });
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `translations_${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      reportDownloadError();
    }
  }, [reportDownloadError]);
  
  // MSAL instance needed for file downloads; must be declared before any early returns.
  const { instance: msalInstance } = useMsal();
  // Root ref for instrumentation after render.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  
  // Track when file previews finish loading (for scroll-to-bottom timing)
  const loadedPreviewsRef = React.useRef<Set<string>>(new Set());
  const totalPreviewsRef = React.useRef<number>(0);
  const previousContentRef = React.useRef<string | ChatContentItem[] | null>(null); // Track content changes
  
  // State for file previews
  const [filePreviews, setFilePreviews] = React.useState<Map<string, { 
    data: string | ArrayBuffer; 
    type: 'text' | 'binary';
    fileName: string;
    contentType?: string;
  }>>(new Map());
  const previewsLoadingRef = React.useRef<Set<string>>(new Set());
  
  // Extract analysisfile links from content and fetch preview data
  React.useEffect(() => {    
    if (typeof content !== 'string') return;
    
    // CRITICAL: Skip file preview extraction during streaming to avoid resetting tracking
    // During streaming, content changes constantly but links aren't replaced yet (still sandbox:)
    // Only process file previews after streaming completes and links are converted to analysisfile:
    if (isStreaming) {
      return;
    }
    
    // Extract all analysisfile links
    const analysisFileRegex = /\[Download[^\]]*\]\(analysisfile:([^:]+):([^:]+):([^)]+)\)/gi;
    const matches = [...content.matchAll(analysisFileRegex)];
    
    // Only reset tracking if content VALUE has actually changed (not just object reference)
    // Compare string values directly to avoid false positives from React re-renders
    const previousValue = typeof previousContentRef.current === 'string' 
      ? previousContentRef.current 
      : JSON.stringify(previousContentRef.current);
    const currentValue = typeof content === 'string' 
      ? content 
      : JSON.stringify(content);
    const contentChanged = previousValue !== currentValue;
    
    // Update tracking - always set totalPreviews to current match count
    totalPreviewsRef.current = matches.length;
    
    // Only reset loaded tracking if content actually changed
    if (contentChanged) {
      previousContentRef.current = content;
      loadedPreviewsRef.current.clear();
    }
    
    if (matches.length === 0) return;
    
    // Fetch preview data for each file
    matches.forEach(async (match) => {
      const containerId = match[1]?.trim();
      const fileId = match[2]?.trim();
      const encodedFileName = match[3]?.trim();
      const fileName = decodeURIComponent(encodedFileName);
      const previewKey = `${containerId}:${fileId}`;

      // Skip if already loaded or loading
      if (filePreviews.has(previewKey) || previewsLoadingRef.current.has(previewKey)) {
        logger.log('[MessageContentRenderer] Skipping (already loaded/loading):', previewKey);
        
        // If preview is already available and not in loaded set, add it and notify
        // This handles case where content didn't change but we need to track loaded state
        if (filePreviews.has(previewKey) && !loadedPreviewsRef.current.has(previewKey)) {
          logger.log('[MessageContentRenderer] Preview already available, marking as loaded:', previewKey);
          loadedPreviewsRef.current.add(previewKey);
          
          // Notify parent for incremental scrolling
          onContentLoaded?.();
          
          // Log when all previews are available
          if (loadedPreviewsRef.current.size === totalPreviewsRef.current && totalPreviewsRef.current > 0) {
            logger.log('[MessageContentRenderer] All previews available immediately');
          }
        }
        return;
      }
      
      // Mark as loading
      previewsLoadingRef.current.add(previewKey);
      logger.log('[MessageContentRenderer] Fetching preview:', previewKey);
      
      try {
        const result = await downloadAnalysisFile(
          msalInstance,
          containerId,
          fileId,
          fileName,
          { previewMode: true }
        );
        
        if (result && 'data' in result) {
          setFilePreviews(prev => {
            const newMap = new Map(prev);
            newMap.set(previewKey, {
              data: result.data,
              type: result.type,
              fileName,
              contentType: result.contentType
            });
            logger.log('[MessageContentRenderer] Preview stored:', previewKey);
            return newMap;
          });
        }
      } catch (error) {
        logger.error('[MessageContentRenderer] Failed to fetch preview:', previewKey, error);
      } finally {
        previewsLoadingRef.current.delete(previewKey);
      }
    });
  }, [content, msalInstance, filePreviews, onContentLoaded, isStreaming]);
  
  // Callback when a file preview finishes loading
  const handlePreviewLoaded = React.useCallback((previewKey: string) => {
    loadedPreviewsRef.current.add(previewKey);
    
    // Call onContentLoaded after EACH preview loads (for incremental scrolling)
    // This allows the parent to scroll the preview into view immediately
    onContentLoaded?.();
    
  }, [onContentLoaded]);
  
  // Post-completion transformation only: do NOT mutate streaming content.
  // Converts analysisfile markdown links AFTER final content is known to avoid metadata loss.
  const postProcessAnalysisLinks = React.useCallback((md: string) => {
    // Normalize malformed analysisfile links (e.g., space after scheme) before markdown parsing.
    // Collapse whitespace immediately following 'analysisfile:' inside link destinations.
    const pattern = /\[([^\]]+)\]\(\s*analysisfile:\s+([^)]*)\)/gi;
    const cleaned = md.replace(pattern, (full, label, spec) => {
      const trimmedSpec = spec.replace(/\s+/g, ' ').trim();
      // Remove stray spaces around colons
      const colonNormalized = trimmedSpec.split(':').map((seg: string) => seg.trim()).join(':');
      const rebuilt = `[${label}](analysisfile:${colonNormalized})`;
      return rebuilt;
    });
    return cleaned;
  }, []);

  // (Deferred) normalized/enriched content computed after definition of normalizeForDisplay below.


  // Base64 PNG validation helper (charset, padding, atob decode, PNG signature)
  const validateBase64Png = (b64: string) => {
    // Basic charset / padding
    if (!/^[A-Za-z0-9+/]+={0,2}$/.test(b64)) return false;
    try {
      // Decode only a slice for performance
      const bin = atob(b64.slice(0, Math.min(b64.length, 128)));
      if (bin.length < 8) return false;
      // PNG signature: 89 50 4E 47 0D 0A 1A 0A
      return (
        bin.charCodeAt(0) === 0x89 &&
        bin.charCodeAt(1) === 0x50 &&
        bin.charCodeAt(2) === 0x4E &&
        bin.charCodeAt(3) === 0x47 &&
        bin.charCodeAt(4) === 0x0D &&
        bin.charCodeAt(5) === 0x0A &&
        bin.charCodeAt(6) === 0x1A &&
        bin.charCodeAt(7) === 0x0A
      );
    } catch {
      return false;
    }
  };

  // Extract textual portion for markdown/image parsing when content may be structured
  const rawStringContent = typeof content === 'string'
    ? content
    : Array.isArray(content)
      ? content.filter(c => c.type === 'input_text' && c.text).map(c => c.text || '').join('\n')
      : '';
  // Removed verbose raw content logging per user request.
  // Track last valid base64 to prevent displaying broken images during streaming
  const lastValidBase64Ref = React.useRef<string>('');
  const currentBase64Ref = React.useRef<string>('');
  
  let bestBase64 = '';
  if (rawStringContent.includes('![Generated Image](data:image/png;base64,')) {
    const imageParts = rawStringContent.split('![Generated Image](data:image/png;base64,');
    if (imageParts.length > 1) {
      for (let i = imageParts.length - 1; i >= 1; i--) {
        // Skip parts that contain duplicate markers (indicates concatenated chunks)
        if (imageParts[i].includes('data:image/png;base64,') || imageParts[i].includes('![Generated Image]')) {
          continue;
        }
        
        const closingIndex = imageParts[i].indexOf(')');
        if (closingIndex !== -1) {
          let base64Data = imageParts[i].substring(0, closingIndex);
          base64Data = base64Data.replace(/\s+/g, '').replace(/\n/g, '').replace(/\r/g, '');
          const looksLikeBase64 = base64Data.length > 1000 && /^[A-Za-z0-9+/=]+$/.test(base64Data);
          if (looksLikeBase64 && validateBase64Png(base64Data)) {
            bestBase64 = base64Data;
            lastValidBase64Ref.current = base64Data;
            currentBase64Ref.current = base64Data;
            break;
          }
        }
      }
    }
  }
  
  // Fallback to cache only if extraction failed completely
  if (!bestBase64 && currentBase64Ref.current) {
    bestBase64 = currentBase64Ref.current;
  }

  // Update current image URL when bestBase64 changes (synchronously compute it)
  const imageUrl = bestBase64 ? `data:image/png;base64,${bestBase64}` : '';

  // Reset error state when a new base64 candidate arrives (so we retry rendering)
  React.useEffect(() => {
    if (bestBase64 && bestBase64 !== lastValidBase64Ref.current) {
      setImageError(false);
    }
  }, [bestBase64]);

  let contentKey: string;
  if (rawStringContent.includes('![Generated Image](data:image/png;base64,')) {
    const imageParts = rawStringContent.split('![Generated Image](data:image/png;base64,');
    let latestBase64 = '';
    for (let i = imageParts.length - 1; i >= 1; i--) {
      const closingIndex = imageParts[i].indexOf(')');
      if (closingIndex !== -1) {
        const base64Data = imageParts[i].substring(0, closingIndex);
        if (base64Data.length > 1000 && /^[A-Za-z0-9+/=]+$/.test(base64Data)) {
          latestBase64 = base64Data;
          break;
        }
      }
    }
    contentKey = `image-${latestBase64.substring(0, 100)}-${latestBase64.length}`;
  } else {
    contentKey = `text-${rawStringContent.length}`;
  }

  const handleImageClick = (src: string) => dispatch(openImage(src));

  const normalizeForDisplay = (text: string) => {
    if (disableNormalization) return text; // bypass all transformations (reasoning trace)
    if (text.includes('data:image/png;base64,') || text.includes('![Generated Image]')) return text;
    
    // FIX: Repair UTF-8 mojibake from Cortex/Snowflake RAG backend.
    // The backend double-encodes UTF-8: e.g. em dash U+2014 (bytes E2 80 94) arrives as
    // three Latin-1 chars â€" (\u00E2\u0080\u0094). This is idempotent — correctly-encoded
    // text from other RAGs will never match these control-character sequences.
    let normalized = text
      .replace(/\u00E2\u0080\u0094/g, '\u2014')  // em dash —
      .replace(/\u00E2\u0080\u0093/g, '\u2013')  // en dash –
      .replace(/\u00E2\u0080\u0099/g, '\u2019')  // right single quote '
      .replace(/\u00E2\u0080\u0098/g, '\u2018')  // left single quote '
      .replace(/\u00E2\u0080\u009C/g, '\u201C')  // left double quote "
      .replace(/\u00E2\u0080\u009D/g, '\u201D')  // right double quote "
      .replace(/\u00E2\u0080\u00A2/g, '\u2022')  // bullet •
      .replace(/\u00E2\u0080\u00A6/g, '\u2026')  // ellipsis …
      .replace(/\u00C2\u00AB/g, '\u00AB')         // « left guillemet
      .replace(/\u00C2\u00BB/g, '\u00BB')         // » right guillemet
      .replace(/\u00C3\u00A9/g, '\u00E9')         // é
      .replace(/\u00C3\u00A8/g, '\u00E8')         // è
      // Repair 4-byte emoji mojibake: F0 9F xx xx → proper emoji code point
      .replace(/\u00F0\u009F([\u0080-\u00BF])([\u0080-\u00BF])/g, (_m, b3: string, b4: string) => {
        const codePoint = ((0xF0 & 0x07) << 18) | ((0x9F & 0x3F) << 12) | ((b3.charCodeAt(0) & 0x3F) << 6) | (b4.charCodeAt(0) & 0x3F);
        return String.fromCodePoint(codePoint);
      });

    normalized = normalized
      .replace(/\u2212/g, '-')
      .replace(/\u2014/g, '-')
      .replace(/__IMAGE_UPDATE__[^_]*__[^_]*__END_UPDATE__/g, '')
      // Escape lone ~ characters that are NOT part of ~~ (GFM strikethrough) to prevent
      // accidental strikethrough when two ~ chars appear far apart in the text
      // e.g. (~3%) ... (~$2.5B) would otherwise render the middle as strikethrough.
      // Replace any ~ not already doubled with \~ (escaped tilde).
      .replace(/~~/g, '\uE000DBTILDE\uE000') // protect real ~~ first
      .replace(/~/g, '\\~')              // escape lone ~
      .replace(/\uE000DBTILDE\uE000/g, '~~') // restore real ~~
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/&amp;/g, '&');

    // CRITICAL: Strip any <thinking> blocks that made it through (web_search safety net).
    // Runs after entity decoding above so encoded tags cannot be reintroduced afterwards.
    normalized = stripThinkingTags(normalized);
    // Ensure code fence openings (```language) are at the start of a line.
    // Web search (and some other) responses place the fence mid-line, e.g.:
    //   "Example (Next.js): ```typescript\n// code\n```"
    // A fence not at the start of a line is not recognised by markdown, so the opening
    // renders as inline text while the closing ``` starts an empty code block instead.
    // Only targets fenced openings with a language tag (```word) so plain closing fences
    // (```) and inline code spans are unaffected.
    normalized = normalized.replace(/([^\n])(```[a-zA-Z]+)/g, '$1\n$2');
    // Normalize any accidental space after analysisfile: scheme BEFORE markdown parse so rehype plugin can match
    normalized = normalized.replace(/\(analysisfile:\s+/gi, '(analysisfile:');
    // Also fix inline code backtick segments that may have been split by streaming (e.g., `Sales` `_B` `onus`) by joining contiguous backtick-wrapped fragments
    normalized = normalized.replace(/`([A-Za-z0-9]+)`\s*`([A-Za-z0-9_]+)`/g, (m, a, b) => '`' + a + b + '`');
    // Join three-part splits like `Sales` `_B` `onus` -> `Sales_Bonus`
    normalized = normalized.replace(/`([A-Za-z0-9]+)`\s*`([A-Za-z0-9_]+)`\s*`([A-Za-z0-9_]+)`/g, (m,a,b,c)=>'`'+a+b+c+'`');
    normalized = normalized.replace(/^([A-Z][A-Za-z0-9\s,()&'-]{2,})\n(?=(?:[-*•]|\d+\.\s|\$))/gm, '## $1\n');
    // Ensure a blank line before markdown list items when the preceding line is non-list text
    // (e.g. "Dash list:\n- item" → "Dash list:\n\n- item"). This fixes LLM responses that omit
    // the required blank line. Does NOT affect consecutive list items since those lines start
    // with the marker itself and are excluded by [^-*\s\d>•].
    normalized = normalized.replace(/^([^-*\s\d>•][^\n]*)\n([ \t]*(?:[-*]|\d+\.)\s)/gm, '$1\n\n$2');
    // Same fix for Unicode bullet (•) lists: ensure blank line before first • in a run.
    // Uses [^•\n] to exclude lines already starting with •, preventing double-blanks between items.
    normalized = normalized.replace(/^([^•\n][^\n]*)\n(•)/gm, '$1\n\n$2');
    // Normalize parenthesis-style ordered lists (e.g. "1)" → "1.") so remark parses them reliably
    normalized = normalized.replace(/^(\s*)(\d+)\)\s/gm, '$1$2. ');
    // Collapse blank lines between consecutive numbered-list items so remark treats them as one <ol>.
    // Without this, blank-separated "1. foo\n\n2. bar" creates two separate <ol> elements each starting at 1.
    normalized = normalized.replace(/(^\s*\d+\.\s[^\n]*)(\n{2,})(?=\s*\d+\.\s)/gm, '$1\n');
    // Removed semicolon list splitting; keep semicolons inline.
    // Improve list item label formatting: only wrap plain labels (no existing ** or backticks) and keep value inline
    // Avoid converting bullet lines that begin with a markdown link (e.g. download links) into label/value pairs.
    // Also skip any line containing an analysisfile scheme so we don't split the anchor across <strong> and text.
    normalized = normalized.replace(/^(-\s+)([^:\n]+):\s*(.+)$/gm, (match, dash, label, value) => {
      const original = match;
      const lineHasAnalysisfile = /analysisfile:/i.test(original);
      const labelStartsWithLink = /^\[.+\]/.test(label.trim());
      const lineHasHtmlAnchor = /<a\s+href=/i.test(original);
      if (lineHasAnalysisfile || labelStartsWithLink || lineHasHtmlAnchor) {
        // Debug: show that we intentionally skipped transformation for this bullet.
        try { logger.log('[render-debug][skip-bullet-label-transform]', { line: original }); } catch {}
        return original; // do not alter
      }
      if (/\*\*/.test(label) || /`/.test(label)) {
        return `${dash}${label}: ${value}`; // leave as-is
      }
      return `${dash}**${label}:** ${value}`;
    });
    // Ensure paragraph breaks are preserved before bold text patterns like "**Option A:**"
    // This prevents bold text from being pulled onto the previous line when there should be a paragraph break
    normalized = normalized.replace(/([.?!:])\s*\n\s*\n\s*(\*\*[A-Z])/g, '$1\n\n$2');
    // Targeted cleanup: fix malformed download bullet links like "- **[Download file.csv](analysisfile:** gAAAA:...)" produced by earlier strong wrapping.
    // Pattern: a bold segment starting before the '[' and closing before scheme causing '** ' insertion after analysisfile:
    normalized = normalized.replace(/\*\*\[Download([^\]]*)\]\(analysisfile:\*\*\s+([^)]*?)\)/gi, (full, fileLabelSuffix, remainder) => {
      const cleanedRemainder = remainder.split(':').map((s: string) => s.trim()).join(':');
      const reconstructed = `[Download${fileLabelSuffix}](analysisfile:${cleanedRemainder})`;
      try { logger.log('[render-debug][download-bullet-cleaned]', { before: full, after: reconstructed }); } catch {}
      return reconstructed;
    });
    // Secondary fallback: if any link still has space after analysisfile: (even without bold corruption), collapse it.
    normalized = normalized.replace(/\[Download([^\]]*)\]\(analysisfile:\s+([^)]*?)\)/gi, (full, fileLabelSuffix, afterScheme) => {
      const cleanedAfter = afterScheme.split(':').map((s: string) => s.trim()).join(':');
      const rebuilt = `[Download${fileLabelSuffix}](analysisfile:${cleanedAfter})`;
      try { logger.log('[render-debug][download-bullet-space-collapsed]', { before: full, after: rebuilt }); } catch {}
      return rebuilt;
    });
    normalized = normalized.replace(/^## (.*)\$/gm, '## $1');
    // Ensure specific analysis headings render distinctly
    normalized = normalized.replace(/^New file created:?\s*(?:\n)?/gm, '## New file created:\n\n');
    normalized = normalized.replace(/^Quick summary of columns:?\s*(?:\n)?/gm, '### Quick summary of columns:\n\n');
    // If a filename immediately follows the New file created heading on same line, enforce newline separation
    normalized = normalized.replace(/(## New file created:\n\n)([^\n]+\.\w{1,8})(\s+Quick summary)/gm, '$1$2\n\n$3');
    // --- Additional robust normalization for analysisfile links to avoid race conditions ---
    // 1. Remove any whitespace immediately after the scheme before container id.
    normalized = normalized.replace(/\(analysisfile:\s+/gi, '(analysisfile:');
    // 2. Canonicalize inner segments (trim spaces around each colon-delimited part) for links.
    normalized = normalized.replace(/\[([^\]]+)\]\(analysisfile:([^)]*)\)/gi, (full, label, inner) => {
      const cleanedInner = inner.split(':').map((seg: string) => seg.trim()).join(':');
      return `[${label}](analysisfile:${cleanedInner})`;
    });
    // 3. Fix nested markdown links pattern: [filename]([Download filename](analysisfile:...))
    //    This removes the outer wrapper that causes "[filename](" and ")" to appear as text
    //    Pattern matches: [text]([Download text](analysisfile:...)) and extracts just the inner download link
    normalized = normalized.replace(/\[([^\]]+)\]\(\s*(\[Download[^\]]*\]\(analysisfile:[^)]+\))\s*\)/gi, (full, outerText, innerLink) => {
      try { 
        logger.log('[render-debug][nested-link-unwrapped]', { 
          before: full, 
          after: innerLink,
          outerText,
          innerLink 
        }); 
      } catch {}
      return innerLink;
    });
    // // 4. Log one-time if any remaining malformed pattern still exists with double spaces.
    // if (/\(analysisfile:\s+/.test(normalized)) {
    //   // eslint-disable-next-line no-console
    //   logger.log('[analysisfile-normalize] residual-whitespace-after-scheme');
    // }
    return normalized;
  };

  // Now that normalizeForDisplay is defined, compute enriched markdown (outside of early image return branches).
  
  // Special handling for web_search and reasoning loading messages with animated ellipses
  // (Removed: now handled by ReasoningDropdown/AnimatedEllipsisText)
  
  if (Array.isArray(content)) {
    // Structured user message: render files + text directly, bypass markdown normalization
    return (
      <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {content.map((item, idx) => {
          if (item.type === 'input_image') {
            return (
              <div key={idx} className="flex items-start gap-2">
                <ClickableImageThumbnail
                  src={item.image_url}
                  alt="Uploaded file"
                  className="w-24 h-24 object-cover rounded-md cursor-pointer border border-[var(--color-accent)]"
                />
              </div>
            );
          } else if (item.type === 'input_file') {
            const isImg = item.file_data?.startsWith('data:image');

            return (
              <div key={idx} className="flex items-start gap-2">
                {isImg ? (
                  <ClickableImageThumbnail
                    src={item.file_data}
                    alt={String(item.filename || 'Uploaded image')}
                    className="w-24 h-24 object-cover rounded-md cursor-pointer border border-[var(--color-accent)]"
                  />
                ) : (
                  <PreviewableFileAttachment
                    fileName={String(item.filename || 'Uploaded file')}
                    fileData={item.file_data}
                    dataSource="data-url"
                  >
                    {({ canPreview, openPreview }) => (
                      <button
                        type="button"
                        onClick={openPreview}
                        disabled={!canPreview}
                        className="px-2 py-1 rounded bg-[var(--color-secondary)] border border-[var(--color-accent)] text-xs flex items-center gap-1 text-left disabled:cursor-default"
                        style={{ cursor: canPreview ? 'pointer' : 'default' }}
                        title={canPreview ? 'Click to preview file' : undefined}
                      >
                        <span className="font-semibold">{item.filename}</span>
                      </button>
                    )}
                  </PreviewableFileAttachment>
                )}
              </div>
            );
          } else if (item.type === 'input_text') {
            return (
              <div key={idx} className="text-sm whitespace-pre-line">
                {item.text}
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  }
  const normalizedContent = disableNormalization ? rawStringContent : normalizeForDisplay(rawStringContent.trim());
  // Removed normalized content debug logging.
  // Strip internal replacement sentinel artifacts that leaked into final text (defensive; should be removed upstream)
  // Replace [__REPLACE__] with double newline to ensure proper line breaks
  let withoutSentinels = normalizedContent.replace(/\[__REPLACE__\]/g,'\n\n');
  // EXTRA SAFETY: Strip any remaining thinking tags before rendering (defense in depth)
  withoutSentinels = stripThinkingTags(withoutSentinels);
  // Currency-safety for single-dollar math, skipping URLs and code where `$` is literal.
  withoutSentinels = escapeCurrencyDollars(withoutSentinels);
  
  // Always compute enrichedContent (empty string if structured array to keep hook order stable)
  let enrichedContent = '';
  if (!Array.isArray(content)) {
    if (disableNormalization) {
      enrichedContent = withoutSentinels.replace(/\[([^\]]+)\]\(\s*analysisfile:\s+([^)]*)\)/gi, (full, label, spec) => {
        const cleanedInner = spec.split(':').map((seg: string) => seg.trim()).join(':');
        return `[${label}](analysisfile:${cleanedInner})`;
      }).replace(/(\[[^\]]+\]\(analysisfile:[^)]+\))(?!\s*\n)/g, '$1\n\n');
    } else {
      const lightSanitizeAnalysisfile = (txt: string) => txt
        .replace(/\(\s*analysisfile:\s+/gi, '(analysisfile:')
        .replace(/\[([^\]]+)\]\(analysisfile:([^)]*)\)/gi, (full, label, inner) => {
          const cleanedInner = inner.split(':').map((seg: string) => seg.trim()).join(':');
          return `[${label}](analysisfile:${cleanedInner})`;
        })
        .replace(/(\[[^\]]+\]\(analysisfile:[^)]+\))(?!\s*\n)/g, '$1\n\n');
      if (isStreaming) {
        enrichedContent = lightSanitizeAnalysisfile(withoutSentinels);
      } else {
        let processed = postProcessAnalysisLinks(withoutSentinels)
          .replace(/(\[[^\]]+\]\(analysisfile:[^)]+\))(?!\s*\n)/g, '$1\n\n')
          .replace(/\n{3,}/g, '\n\n')
          .replace(/\*\*([^*]+?):\s+\*\*/g, '**$1:**');
        enrichedContent = processed;
      }
    }
  }

  // If translation files are present, render dedicated translation UI
  if (translationData && translationData.length > 0) {
    return (
      <div className={className} style={{ maxWidth: 'none' }}>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-base font-semibold text-[var(--color-text)]">File Translation is completed</h3>
        </div>

        <div className="flex flex-col gap-3">
          {translationData.map(td => {
            const b64 = td.content_base64.replace(/^data:[^;]+;base64,/, '');
            const padding = (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);
            const sizeBytes = Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
            const mimeType = inferMimeTypeFromFilename(td.filename);
            return (
              <PreviewableFileAttachment
                key={td.filename}
                fileName={td.filename}
                fileType={mimeType}
                fileData={b64}
                dataSource="uploaded"
              >
                {({ canPreview, openPreview }) => (
                  <div
                    className={`flex gap-2 items-center justify-between ${isMobile ? 'w-full' : 'w-[50%]'} px-4 py-2 rounded-md shadow-sm text-white ${mode === 'light' ? 'bg-gray-800' : 'bg-gray-700'}`}
                    onClick={canPreview ? openPreview : undefined}
                    style={{ cursor: canPreview ? 'pointer' : 'default' }}
                    aria-label={`File ${td.filename}`}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="font-semibold truncate text-sm" title={td.filename}>{td.filename}</span>
                      <span className="text-xs" style={{ opacity: 0.8 }}>File Size: {formatFileSize(sizeBytes)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleTranslationDownload(td.filename, b64);
                      }}
                      className="p-2 rounded hover:bg-white/10 flex items-center justify-center"
                      aria-label={`Download ${td.filename}`}
                    >
                      <DownloadIcon height={24} width={24} />
                    </button>
                  </div>
                )}
              </PreviewableFileAttachment>
            );
          })}
        </div>
          {translationData.length > 1 && (
            <div className="pt-4">
              <button
                type="button"
                onClick={() => downloadAll(translationData)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[#2563EB] text-white"
              >
                Download All
              </button>
            </div>
          )}
        
        {/* Disclaimer for translation */}
        <div className="mt-4 px-3 py-2 rounded-md bg-yellow-50 border-l-4 border-yellow-300">
          <p className="text-sm text-black">
            This is an experimental version. Please review all translated documents carefully before using them.
          </p>
        </div>
      </div>
    );
  }

  // After content changes and render cycle completes, inspect DOM for custom elements & resulting buttons.
  // React.useEffect(() => {
  //   if (!rootRef.current) return;
  // }, [enrichedContent]);

  // Utility to escape regex special chars in labels for fallback parsing.
  // Removed escapeRegExp (unused after link transformation simplification).
  // Fallback extractor if custom element lost data-* attributes (e.g., stripped by sanitizer in an edge case).
  // (Deprecated) recoverMetadataFromLabel logic removed after reverting to plain markdown links.

  // Dedicated image rendering branch: show streaming image in background even on error (hidden), overlay spinner / fallback
  if (bestBase64 && imageUrl) {
    // Use stable key to keep element mounted and allow smooth src updates during streaming
    const imageKey = 'generated-image';
    return (
      <div className={className} style={{ maxWidth: 'none', lineHeight: '1.6' }}>
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', margin: '0.5em 0' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: '420px', aspectRatio: '1', backgroundColor: 'transparent', borderRadius: '8px', overflow: 'hidden' }}>
            <img
              src={imageUrl}
              alt="Generated content"
              key={imageKey}
              style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: imageError ? 'default' : 'pointer', border: '2px solid var(--color-accent)', borderRadius: '8px', opacity: imageError ? 0 : (isStreaming ? 0.9 : 1), transition: 'opacity 0.3s ease-in-out', animation: 'none' }}
              onClick={() => { if (!imageError) handleImageClick(imageUrl); }}
              onError={() => {
                // Keep image element mounted (hidden) so future successful src updates appear without flashing broken icon
                setImageError(true);
              }}
              onLoad={() => {
                // Clear error state when image loads successfully
                setImageError(false);
              }}
            />
            {(isStreaming || imageError) && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '8px', pointerEvents: 'none', border: '2px solid var(--color-accent)' }}>
                <div className="spinner" style={{ width: '28px', height: '28px', border: '3px solid rgba(255,255,255,0.25)', borderTop: '3px solid rgba(255,255,255,0.95)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              </div>
            )}
          </div>
        </div>
        <div style={{ fontSize: '0.875rem', color: 'var(--color-text)', fontStyle: 'italic', marginTop: '0.5em', textAlign: 'center' }}>
          Note: Images are for internal use only and each request creates a new image as image modifications aren’t yet supported.
          {modelId === 'gpt-image-2' && (
            <div style={{ marginTop: 8, color: '#b45309', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '6px' }}>
              <ExclamationTriangleIcon width={16} height={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Beta Notice: GPT-Image-2 is currently in beta. Image generation may take over 5 minutes and may occasionally time out. We are actively working with the provider to improve speed and reliability. Thank you for your patience.</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const renderWithFallback = (content: string) => {
    const hasStreamingImages = /<div[^>]*data-streaming-image[^>]*>/i.test(content) || /<img[^>]*data-streaming-image[^>]*>/i.test(content);
    if (hasStreamingImages) {
      if (content.includes('class="image-generation-container"')) return null;
      const parts = content.split(/(<img[^>]*data-streaming-image[^>]*>)/i);
      return (
        <div className={className} style={{ maxWidth: 'none', lineHeight: '1.6' }}>
          {parts.map((part, index) => {
            if (part.match(/<img[^>]*data-streaming-image[^>]*>/i)) {
              const srcMatch = part.match(/src="([^"]*)"/);
              const streamingIdMatch = part.match(/data-streaming-image="([^"]*)"/);
              const styleMatch = part.match(/style="([^"]*)"/);
              if (srcMatch && streamingIdMatch) {
                return (
                  <img
                    key={`manual-img-${index}`}
                    src={srcMatch[1]}
                    alt="Generated streaming content"
                    data-streaming-image={streamingIdMatch[1]}
                    style={styleMatch ? parseStyleString(styleMatch[1]) : { maxWidth: '100%', height: 'auto', cursor: 'pointer', border: '2px solid var(--color-accent)', borderRadius: '8px' }}
                    onClick={() => handleImageClick(srcMatch[1])}
                  />
                );
              }
              return null;
            }
            return (
              <ReactMarkdown
                key={`content-${index}`}
                remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: true }]]}
                // rehypePlugins={[rehypeHighlight, rehypeKatex]}
                rehypePlugins={[[rehypeRaw, { passThrough: ['img', 'div', 'span'], allowDangerousHtml: true, allowDangerousCharacters: true }], rehypeHighlight, rehypeKatex]}
              >
                {part}
              </ReactMarkdown>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const parseStyleString = (styleStr: string): React.CSSProperties => {
    const styles: React.CSSProperties = {};
    styleStr.split(';').forEach(declaration => {
      const [property, value] = declaration.split(':').map(s => s.trim());
      if (property && value) {
        const camelCaseProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        (styles as any)[camelCaseProperty] = value;
      }
    });
    return styles;
  };

  const fallbackRender = renderWithFallback(enrichedContent);
  if (fallbackRender) return fallbackRender;

  // Link component with spinner for analysisfile anchors
  const MarkdownLink: React.FC<any> = (props) => {
    const { href, children } = props;
    // Use theme context so link/download button colors update immediately when user toggles theme.
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    const [isDownloading, setIsDownloading] = React.useState(false); // always declare hook top-level
    const [downloadFailed, setDownloadFailed] = React.useState(false);
    // Per-link failure icon auto-dismiss (hook declared unconditionally)
    React.useEffect(() => {
      if (downloadFailed) {
        const t = setTimeout(() => setDownloadFailed(false), 3500);
        return () => clearTimeout(t);
      }
    }, [downloadFailed]);
    let isAnalysis = false;
    let containerId = '';
    let fileId = '';
    let decodedName = '';
    if (href && /^analysisfile:/i.test(href)) {
      // Ensure no stray spaces after scheme (should have been cleaned earlier)
      const raw = href.replace(/^analysisfile:\s*/i, '');
      const segments = raw.split(':');
      if (segments.length >= 3) {
        containerId = (segments[0] || '').trim();
        fileId = (segments[1] || '').trim();
        const filenameRaw = segments.slice(2).join(':').trim();
        decodedName = (() => { try { return decodeURIComponent(filenameRaw); } catch { return filenameRaw; } })();
        if (containerId && fileId) isAnalysis = true;
      }
    }
    if (href && (href.startsWith('sandbox:') || /\/mnt\/data\//.test(href))) {
      return (
        <span 
          data-sandbox-placeholder="true" 
          title="File download not available - stream was stopped before file processing completed. Retry the request to download files."
          style={{ 
            color: 'var(--color-text)', 
            fontWeight: 500, 
            textDecoration: 'none', 
            cursor: 'not-allowed',
            opacity: 0.6,
            borderBottom: '1px dashed currentColor'
          }}
        >
          {children} (unavailable)
        </span>
      );
    }
    if (isAnalysis) {
      const previewKey = `${containerId}:${fileId}`;
      const previewData = filePreviews.get(previewKey);
      
      logger.log('[MarkdownLink] Rendering analysis file:', {
        previewKey,
        hasPreviewData: !!previewData,
        previewDataType: previewData ? typeof previewData.data : 'none',
        fileName: decodedName
      });
      
      const handleClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!containerId || !fileId || isDownloading) return;
        setIsDownloading(true);
        try {
          await downloadAnalysisFile(msalInstance, containerId, fileId, decodedName, { logOnly: false, autoDownloadCsvIfText: true });
        } catch (err) {
          console.error('[file-download] error', err);
          setDownloadFailed(true);
          reportDownloadError();
        } finally {
          setIsDownloading(false);
        }
      };
      return (
        <div style={{ display: 'block', marginTop: '8px', marginBottom: '8px'}}>
          {previewData && (
            <FilePreview
              key={previewKey}
              fileData={previewData.data}
              fileName={previewData.fileName}
              fileType={previewData.contentType}
              onLoad={() => handlePreviewLoaded(previewKey)}
              onError={() => {
                console.error('[FilePreview] Preview rendering failed');
              }}
            />
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <button
              type="button"
              onClick={handleClick}
              disabled={isDownloading}
              aria-busy={isDownloading}
              style={{
                background: 'var(--form-accent)',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                padding: 0,
                margin: 0,
                color: isDark ? '#9ecbff' : '#0b57d0',
                textDecoration: 'none',
                cursor: isDownloading ? 'wait' : 'pointer',
                fontWeight: 500,
                font: 'inherit',
                opacity: isDownloading ? 0.6 : 1,
                borderRadius: '6px',
                paddingInline: '10px',
                paddingBlock: '6px'
              }}
            >
              {children}
            </button>
            {isDownloading && (
              <span
                role="status"
                aria-label="Downloading"
                style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid currentColor',
                  borderRightColor: 'transparent',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite'
                }}
              />
            )}
            {(!isDownloading && downloadFailed) && (
              <span
                role="alert"
                aria-label="Download failed"
                style={{ display: 'inline-flex', alignItems: 'center', color: '#dc2626' }}
              >
                <ExclamationTriangleIcon width={16} height={16} />
              </span>
            )}
          </span>
        </div>
      );
    }
    
    // Check if href is a non-functional reference (empty, just #, or anchor-only like #ref-1)
    // Keep as link for consistent styling, but prevent navigation
    const isNonFunctionalRef = !href || href.trim() === '' || href.trim() === '#' || /^#[a-zA-Z0-9_-]*$/.test(href.trim());
    
    return (
      <a
        href={href}
        onClick={(e) => {
          if (isNonFunctionalRef) {
            e.preventDefault();
          }
        }}
        target={isNonFunctionalRef ? undefined : '_blank'}
        rel={isNonFunctionalRef ? undefined : 'noopener noreferrer'}
        className="hover:underline"
        style={{
          color: isDark ? '#60a5fa' : '#1d4ed8',
          fontWeight: 500,
          cursor: 'pointer'
        }}
      >
        {children}
      </a>
    );
  };

  const AnalysisfileLink: React.FC<any> = ({ node, children }) => {
    // Subscribe to theme context for instant re-render on mode change (fixes stale button styling).
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    const props: any = node?.properties || {};
    const containerId = (props['data-container'] || '').toString();
    const fileId = (props['data-file'] || '').toString();
    const encoded = (props['data-name'] || '').toString();
    const decodedName = (() => { try { return decodeURIComponent(encoded); } catch { return encoded; } })();
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [downloadFailed, setDownloadFailed] = React.useState(false);
    
    const previewKey = `${containerId}:${fileId}`;
    const previewData = filePreviews.get(previewKey);
    
    const handleClick = async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!containerId || !fileId || isDownloading) return;
      setIsDownloading(true);
      try {
        await downloadAnalysisFile(msalInstance, containerId, fileId, decodedName, { logOnly: false, autoDownloadCsvIfText: true });
      } catch (err) {
        console.error('[file-download] error', err);
        setDownloadFailed(true);
        reportDownloadError();
      } finally {
        setIsDownloading(false);
      }
    };
    React.useEffect(() => {
      if (downloadFailed) {
        const t = setTimeout(() => setDownloadFailed(false), 3500);
        return () => clearTimeout(t);
      }
    }, [downloadFailed]);
    
    // Detect if this is a data file (Excel, CSV, TSV, JSON) that should show preview above
    const isDataFile = /\.(xlsx?|csv|tsv|json)$/i.test(decodedName);
    
    return (
      <>
        {previewData && isDataFile && (
          <div style={{ display: 'block', marginTop: '8px', marginBottom: '8px' }}>
            <FilePreview
              key={previewKey}
              fileData={previewData.data}
              fileName={previewData.fileName}
              fileType={previewData.contentType}
              onLoad={() => handlePreviewLoaded(previewKey)}
              onError={() => {
                console.error('[FilePreview] Preview rendering failed');
              }}
            />
          </div>
        )}
        {previewData && !isDataFile && (
          <div style={{ display: 'block', marginTop: '8px', marginBottom: '8px' }}>
            <FilePreview
              key={previewKey}
              fileData={previewData.data}
              fileName={previewData.fileName}
              fileType={previewData.contentType}
              onLoad={() => handlePreviewLoaded(previewKey)}
              onError={() => {
                console.error('[FilePreview] Preview rendering failed');
              }}
            />
          </div>
        )}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <button
            type="button"
            onClick={handleClick}
            disabled={isDownloading}
            aria-busy={isDownloading}
            style={{
              background: 'var(--form-accent)',
              border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
              padding: 0,
              margin: 0,
              color: isDark ? '#9ecbff' : '#0b57d0',
              textDecoration: 'none',
              cursor: isDownloading ? 'wait' : 'pointer',
              fontWeight: 500,
              font: 'inherit',
              opacity: isDownloading ? 0.6 : 1,
              borderRadius: '6px',
              paddingInline: '10px',
              paddingBlock: '6px'
            }}
          >
            {children}
          </button>
          {isDownloading && (
            <span
              role="status"
              aria-label="Downloading"
              style={{
                width: '14px',
                height: '14px',
                border: '2px solid currentColor',
                borderRightColor: 'transparent',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'spin 0.8s linear infinite'
              }}
            />
          )}
          {(!isDownloading && downloadFailed) && (
            <span
              role="alert"
              aria-label="Download failed"
              style={{ display: 'inline-flex', alignItems: 'center', color: '#dc2626' }}
            >
              <ExclamationTriangleIcon width={16} height={16} />
            </span>
          )}
        </span>
      </>
    );
  };

  // Helper component: native horizontal scrollbar on <pre>; inner code expands beyond width to force overflow.
  const Pre: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const preRef = React.useRef<HTMLPreElement | null>(null);
    const [codeText, setCodeText] = React.useState('');
    const [isCopied, setIsCopied] = React.useState(false);
    const { mode } = useTheme();
    const isDark = mode === 'dark';

    React.useLayoutEffect(() => {
      if (preRef.current) {
        setCodeText(preRef.current.textContent || '');
      }
    }, [children]);

    const handleCopy = React.useCallback(() => {
      if (!codeText) return;
      try {
        navigator.clipboard.writeText(codeText);
        logger.log('[Copy] Text copied, setting isCopied to true');
        setIsCopied(true);
        setTimeout(() => {
          logger.log('[Copy] Clearing isCopied');
          setIsCopied(false);
        }, 1200);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Copy failed', e);
      }
    }, [codeText]);

    // Ensure child code element has width that can exceed container to trigger horizontal overflow.
    const wrappedChildren = React.useMemo(() => {
      if (React.isValidElement(children) && children.type === 'code') {
        const existingStyle = (children.props as any).style || {};
        return React.cloneElement(children as any, {
          style: {
            ...existingStyle,
            display: 'inline-block',
            minWidth: '100%',
            width: 'fit-content',
            whiteSpace: 'pre',
            verticalAlign: 'top'
          }
        } as any);
      }
      return <code style={{ display: 'inline-block', minWidth: '100%', width: 'fit-content', whiteSpace: 'pre' }}>{children}</code>;
    }, [children]);

    return (
      <div style={{ position: 'relative', maxWidth: '100%', marginBottom: '26px' }}>
        <pre
          ref={preRef}
          style={{
            margin: 0,
            padding: '14px 16px',
            fontSize: '13px',
            lineHeight: '1.4',
            color: isDark ? '#d4d4d4' : '#000000',
            fontFamily: 'SFMono-Regular,Consolas,Monaco,monospace',
            maxHeight: '360px',
            overflow: 'auto', // both directions
            width: '100%',
            boxSizing: 'border-box',
            background: isDark ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDark ? '#333333' : '#dcdcdc'}`,
            borderRadius: '8px',
            whiteSpace: 'pre'
          }}
        >
          {wrappedChildren}
        </pre>
        <button
          onClick={handleCopy}
          aria-label="Copy code block"
          style={{
            position: 'absolute',
            top: '6px',
            right: '8px',
            background: 'var(--form-accent)',
            border: `1px solid ${isDark ? '#444' : '#d5d5d5'}`,
            color: isDark ? '#d4d4d4' : '#333',
            padding: '4px 6px',
            borderRadius: '6px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            lineHeight: 0,
            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
            transition: 'background .15s ease, color .15s ease'
          }}
          onMouseEnter={e => (e.currentTarget.style.background = isDark ? '#3a3a3a' : '#e0e0e0')}
          onMouseLeave={e => (e.currentTarget.style.background = 'var(--form-accent)')}
        >
          {isCopied ? <CheckIcon width={14} height={14} /> : <CopyIcon width={14} height={14} />}
        </button>
      </div>
    );
  };

  // Themed horizontal rule: visible subtle separator (previous version used var(--color-border) which could be transparent)
  const ThemedHR: React.FC = () => {
    // Use theme context so it re-renders instantly when mode toggles
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    // Gradient spec per user request:
    // Light mode: strong accent / medium center that fades lighter toward fully transparent edges.
    // Dark mode: bright center that darkens toward edges (darker, not transparent black lines only).
    let gradient = '';
    if (!isDark) {
      const accent = 'var(--color-accent, rgba(0,0,0,0.65))';
      const midInner = 'rgba(0,0,0,0.35)';
      const midOuter = 'rgba(0,0,0,0.18)';
      const edge = 'rgba(0,0,0,0.04)';
      gradient = `linear-gradient(to right, ${edge}, ${midOuter}, ${midInner}, ${accent}, ${midInner}, ${midOuter}, ${edge})`;
    } else {
      const centerBright = 'rgba(255,255,255,0.70)';
      const inner = 'rgba(255,255,255,0.45)';
      const outer = 'rgba(255,255,255,0.20)';
      const edgeDark = 'rgba(0,0,0,0.55)';
      gradient = `linear-gradient(to right, ${edgeDark}, ${outer}, ${inner}, ${centerBright}, ${inner}, ${outer}, ${edgeDark})`;
    }
    return (
      <div
        role="separator"
        aria-hidden="true"
        style={{
          margin: '28px 0',
          height: 0,
          position: 'relative',
          paddingBottom: '28px' // creates space below while keeping line 1px
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: '1px',
            background: gradient,
            borderRadius: '1px'
          }}
        />
      </div>
    );
  };

  // Table components with theme support
  const ThemedTable: React.FC<{children: React.ReactNode}> = ({children}) => {
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    const tableRef = React.useRef<HTMLTableElement>(null);
    
    const handleDownloadCSV = () => {
      if (!tableRef.current) return;
      
      const table = tableRef.current;
      const rows: string[][] = [];
      
      // Extract headers
      const headers = Array.from(table.querySelectorAll('thead th')).map(th => {
        const text = th.textContent || '';
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        return text.includes(',') || text.includes('"') || text.includes('\n') 
          ? `"${text.replace(/"/g, '""')}"` 
          : text;
      });
      if (headers.length > 0) rows.push(headers);
      
      // Extract body rows
      table.querySelectorAll('tbody tr').forEach(tr => {
        const cells = Array.from(tr.querySelectorAll('td')).map(td => {
          const text = td.textContent || '';
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          return text.includes(',') || text.includes('"') || text.includes('\n')
            ? `"${text.replace(/"/g, '""')}"` 
            : text;
        });
        if (cells.length > 0) rows.push(cells);
      });
      
      // Create CSV content
      const csvContent = rows.map(row => row.join(',')).join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `table_${Date.now()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };
    
    return (
      <div style={{marginBottom: '1.5em', marginTop: '1em'}}>
        <div style={{overflowX: 'auto'}}>
          <table 
            ref={tableRef}
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
              fontSize: '0.9em'
            }}>
            {children}
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5em' }}>
          <button
            onClick={handleDownloadCSV}
            style={{
              padding: '0.5em',
              fontSize: '0.85em',
              backgroundColor: 'var(--color-secondary)',
              color: 'var(--color-text)',
              border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
              borderRadius: '0.375rem',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s, border-color 0.2s',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? '#424248' : '#e5e7eb';
              e.currentTarget.style.borderColor = isDark ? '#555' : '#9ca3af';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-secondary)';
              e.currentTarget.style.borderColor = isDark ? '#444' : '#d1d5db';
            }}
            title="Download table as CSV"
            aria-label="Download table as CSV"
          >
            <DownloadIcon width={16} height={16} style={{ display: 'block' }} />
          </button>
        </div>
      </div>
    );
  };

  const ThemedThead: React.FC<{children: React.ReactNode}> = ({children}) => {
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    return (
      <thead style={{
        backgroundColor: 'var(--form-accent)',
        borderBottom: `2px solid ${isDark ? '#555' : '#9ca3af'}`
      }}>
        {children}
      </thead>
    );
  };

  const ThemedTr: React.FC<{children: React.ReactNode}> = ({children}) => {
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    return (
      <tr style={{
        borderBottom: `1px solid ${isDark ? '#444' : '#e5e7eb'}`
      }}>
        {children}
      </tr>
    );
  };

  const ThemedTh: React.FC<{children: React.ReactNode}> = ({children}) => {
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    return (
      <th style={{
        padding: '0.75em 1em',
        textAlign: 'left',
        fontWeight: 'bold',
        backgroundColor: 'transparent',
        borderRight: `1px solid ${isDark ? '#444' : '#d1d5db'}`
      }}>
        {children}
      </th>
    );
  };

  const ThemedTd: React.FC<{children: React.ReactNode}> = ({children}) => {
    const { mode } = useTheme();
    const isDark = mode === 'dark';
    return (
      <td style={{
        padding: '0.75em 1em',
        backgroundColor: 'transparent',
        borderRight: `1px solid ${isDark ? '#444' : '#e5e7eb'}`
      }}>
        {children}
      </td>
    );
  };

  // If structured content array, render file previews + text segments instead of markdown.
  if (Array.isArray(content)) {
    // Check for sandbox links in text content
    const hasSandboxLinks = content.some(item => 
      item.type === 'input_text' && item.text && item.text.includes('sandbox:')
    );
    
    return (
      <div ref={rootRef} className={className} style={{ maxWidth: '100%', lineHeight: '1.6', overflowX: 'hidden', pointerEvents: (isStreaming || hasSandboxLinks) ? 'none' : 'auto' }}>
        {content.map((item, idx) => {
          if (item.type === 'input_image') {
            // Handle input_image type (images uploaded by user)
            return (
              <div key={idx} className="mb-2">
                <ClickableImageThumbnail
                  src={item.image_url}
                  alt="Uploaded file"
                  className="w-24 h-24 object-cover rounded-md cursor-pointer border border-[var(--color-accent)]" />
              </div>
            );
          } else if (item.type === 'input_file') {
            const isImg = item.file_data?.startsWith('data:image');
            return (
              <div key={idx} className="mb-2">
                {isImg ? (
                  <ClickableImageThumbnail
                    src={item.file_data}
                    alt={String(item.filename || 'Uploaded image')}
                    className="w-24 h-24 object-cover rounded-md cursor-pointer border border-[var(--color-accent)]" />
                ) : (
                  <PreviewableFileAttachment
                    fileName={String(item.filename || 'Uploaded file')}
                    fileData={item.file_data}
                    dataSource="data-url"
                  >
                    {({ canPreview, openPreview }) => (
                      <button
                        type="button"
                        onClick={openPreview}
                        disabled={!canPreview}
                        className="px-2 py-1 rounded bg-[var(--color-secondary)] border border-[var(--color-accent)] text-xs inline-flex items-center gap-1 text-left disabled:cursor-default"
                        style={{ cursor: canPreview ? 'pointer' : 'default' }}
                        title={canPreview ? 'Click to preview file' : undefined}
                      >
                        <span className="font-semibold truncate" title={item.filename}>{item.filename}</span>
                      </button>
                    )}
                  </PreviewableFileAttachment>
                )}
              </div>
            );
          } else if (item.type === 'input_text') {
            return <p key={idx} style={{ marginBottom: '0.75em', color: 'var(--color-text)', whiteSpace: 'pre-line' }}>{item.text}</p>;
          }
          return null;
        })}
      </div>
    );
  }
  
  // Check if content has sandbox links (indicating stopped stream before file processing)
  const hasSandboxLinks = typeof content === 'string' && content.includes('sandbox:');
  
  return (
  <div ref={rootRef} className={className} style={{ maxWidth: '100%', lineHeight: '1.6', overflowX: 'hidden', pointerEvents: (isStreaming || hasSandboxLinks) ? 'none' : 'auto' }}>
      {/* Translation Download Buttons */}
      {translationData && translationData.length > 0 && (
        <div className="mb-4 flex flex-col gap-2" style={{ padding: '2px' }}>
          {translationData.map((file, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleTranslationDownload(file.filename, file.content_base64)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--color-accent)] text-white text-sm font-medium shadow hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] transition-opacity"
              style={{ maxWidth: 'fit-content' }}
            >
              <DownloadIcon width={16} height={16} />
              <span>{file.filename}</span>
              <span className="text-xs opacity-80">({file.language})</span>
            </button>
          ))}
        </div>
      )}
      
      <ReactMarkdown
        key={contentKey}
        remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: true }]]}
        // rehypePlugins={[rehypeHighlight, rehypeKatex]}
        rehypePlugins={[[rehypeRaw, { passThrough: ['img', 'div', 'span', 'a', 'analysisfile-link', 'download-block'], allowDangerousHtml: true, allowDangerousCharacters: true }], rehypeAnalysisfile, rehypeHighlight, rehypeKatex]}
        unwrapDisallowed={true}
        components={{
          hr: () => <ThemedHR />,
          h1: ({children, node}) => <h1 style={{color: 'var(--color-text)', fontSize: '1.6em', fontWeight: 'bold', marginTop: (node as any)?.position?.start?.line === 1 ? 0 : '1.5em', marginBottom: '0.75em', lineHeight: '1.25'}}>{children}</h1>,
          h2: ({children, node}) => <h2 style={{color: 'var(--color-text)', fontSize: '1.35em', fontWeight: 'bold', marginTop: (node as any)?.position?.start?.line === 1 ? 0 : '1.5em', marginBottom: '0.75em', lineHeight: '1.25'}}>{children}</h2>,
          h3: ({children, node}) => <h3 style={{color: 'var(--color-text)', fontSize: '1.15em', fontWeight: 'bold', marginTop: (node as any)?.position?.start?.line === 1 ? 0 : '1.25em', marginBottom: '0.5em', lineHeight: '1.25'}}>{children}</h3>,
          h4: ({children, node}) => <h4 style={{color: 'var(--color-text)', fontSize: '1.05em', fontWeight: 'bold', marginTop: (node as any)?.position?.start?.line === 1 ? 0 : '1em', marginBottom: '0.4em'}}>{children}</h4>,
          h5: ({children, node}) => <h5 style={{color: 'var(--color-text)', fontSize: '0.95em', fontWeight: 'bold', marginTop: (node as any)?.position?.start?.line === 1 ? 0 : '0.75em', marginBottom: '0.3em'}}>{children}</h5>,
          h6: ({children, node}) => <h6 style={{color: 'var(--color-text)', fontSize: '0.85em', fontWeight: 'bold', marginTop: (node as any)?.position?.start?.line === 1 ? 0 : '0.75em', marginBottom: '0.3em'}}>{children}</h6>,
          ul: ({children, node}) => {
            const n = node as any;
            const cls = n?.properties?.className;
            const isTaskList = Array.isArray(cls) ? cls.includes('contains-task-list') : typeof cls === 'string' ? cls.includes('contains-task-list') : false;
            return <ul style={{
              marginLeft: '1.25em',
              marginBottom: '1.25em',
              paddingTop: '0.25em',
              listStyleType: isTaskList ? 'none' : 'disc',
              paddingLeft: '0.75em'
            }}>{children}</ul>;
          },
          ol: ({children, node}) => {
            const startAttr = (node as any)?.properties?.start;
            const startNum = startAttr !== undefined && startAttr !== null ? Number(startAttr) : undefined;
            return <ol start={startNum} style={{
              marginLeft: '1.25em',
              marginBottom: '1.25em',
              paddingTop: '0.25em',
              listStyleType: 'decimal',
              listStylePosition: 'inside',
              paddingLeft: '0.75em'
            }}>{children}</ol>;
          },
          li: ({children, node}) => {
            const n = node as any;
            // GFM task list items have a checkbox as first child
            const cls = n?.properties?.className;
            const hasTaskClass = Array.isArray(cls) ? cls.includes('task-list-item') : typeof cls === 'string' ? cls.includes('task-list-item') : false;
            const nodeChildren: any[] = n?.children ?? [];
            const hasCheckboxChild = nodeChildren.some((c: any) => c?.tagName === 'input' && c?.properties?.type === 'checkbox');
            const isTask = hasTaskClass || hasCheckboxChild;
            if (isTask) {
              const inputNode = nodeChildren.find((c: any) => c?.tagName === 'input');
              const checked = !!(inputNode?.properties?.checked);
              // Filter out the raw <input> from children so we render our own checkbox
              const filteredChildren = Array.isArray(children)
                ? children.filter((c: any) => !(c?.props?.type === 'checkbox' || c?.type === 'input'))
                : children;
              return (
                <li style={{ listStyle: 'none', display: 'flex', alignItems: 'flex-start', gap: '0.5em', marginBottom: '0.4em', color: 'var(--color-text)', lineHeight: '1.55', marginLeft: '-1.25em' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '1em', height: '1em', minWidth: '1em',
                    marginTop: '0.275em',
                    borderRadius: '3px',
                    border: checked ? 'none' : '1.5px solid var(--color-text)',
                    background: checked ? 'var(--color-accent)' : 'transparent',
                    color: '#fff',
                    fontSize: '0.75em',
                    flexShrink: 0,
                  }}>
                    {checked && <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1.5,6 4.5,9.5 10.5,2.5"/></svg>}
                  </span>
                  <span style={{ opacity: checked ? 0.6 : 1, textDecoration: checked ? 'line-through' : 'none' }}>{filteredChildren}</span>
                </li>
              );
            }
            return <li style={{ marginBottom: '0.4em', paddingBottom: '0.1em', color: 'var(--color-text)', lineHeight: '1.55' }}>{children}</li>;
          },
          blockquote: ({children}) => (
            <blockquote style={{
              borderLeft: '3px solid var(--color-accent)',
              margin: '0.75em 0',
              paddingLeft: '1em',
              paddingTop: '0.1em',
              paddingBottom: '0.1em',
              opacity: 0.85,
              fontStyle: 'italic',
              color: 'var(--color-text)',
            }}>{children}</blockquote>
          ),
          p: ({children}) => <p style={{marginBottom: '0.75em', color: 'var(--color-text)'}}>{children}</p>,
          em: ({children}) => <em style={{color: 'var(--color-text)', fontStyle: 'italic'}}>{children}</em>,
          strong: ({children}) => <strong style={{color: 'var(--color-text)', fontWeight: 'bold'}}>{children}</strong>,
          pre: ({children}) => <Pre>{children}</Pre>,
          table: ({children}) => <ThemedTable>{children}</ThemedTable>,
          thead: ({children}) => <ThemedThead>{children}</ThemedThead>,
          tbody: ({children}) => <tbody>{children}</tbody>,
          tr: ({children}) => <ThemedTr>{children}</ThemedTr>,
          th: ({children}) => <ThemedTh>{children}</ThemedTh>,
          td: ({children}) => <ThemedTd>{children}</ThemedTd>,
          a: MarkdownLink as any,
          // Custom neutralized element rendered as a button (never ingested as markdown link)
          // @ts-ignore
          'analysisfile-link': AnalysisfileLink,
          // Download block rendered as a consistent button, download button for data analysis
          // @ts-ignore
          'download-block': ({node}: any) => {
            const label = decodeURIComponent(node.properties?.['data-label'] || 'Download');
            return (
              <button
                type="button"
                onClick={() => {
                  // Emit a custom event so parent components could hook into logging or actual download logic.
                  try { window.dispatchEvent(new CustomEvent('nav-download-block-click', { detail: { label } })); } catch {}
                }}
                className="inline-flex items-center gap-1 px-3 py-1 rounded bg-[var(--color-accent)] text-white text-xs font-medium shadow hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-accent)]"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>
                <span>{label}</span>
              </button>
            );
          },
          // Removed custom 'download-button' component; relying on <a> transformation for robustness.
          code: ({children, ...props}) => {
            // Treat as block ONLY if it has a language class ("language-") or explicit multiline content.
            const hasLanguage = props?.className ? /language-/.test(props.className) : false;
            const rawText = typeof children === 'string' ? children : Array.isArray(children) ? children.join('') : '';
            const looksMultiline = /\n/.test(rawText);
            if (!hasLanguage && !looksMultiline) {
              const isDark = typeof document !== 'undefined' && (mode === 'dark' || document.documentElement.getAttribute('data-theme')?.startsWith('dark-'));
              return <code style={{
                background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                color: 'var(--color-text)',
                padding: '2px 6px',
                borderRadius: '5px',
                fontSize: '0.88em',
                overflow: 'hidden',
                whiteSpace: 'normal',
                border: isDark ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(0,0,0,0.12)'
              }}>{children}</code>;
            }
            return <code {...props} style={{
              display: 'block',
              background: 'transparent',
              whiteSpace: 'pre',
              wordBreak: 'break-word',
              overflow: 'clip'
            }}>{children}</code>;
          },
          img: ({src, alt, ...props}) => {
            const isStreamingImage = (props as any)['data-streaming-image'];
            return (
              <img
                src={src}
                alt={alt}
                style={{
                  cursor: 'pointer',
                  height: 'auto',
                  borderRadius: '8px',
                  margin: '0.5em 0',
                  ...(isStreamingImage ? { width: '100%', maxWidth: '600px', border: '2px solid var(--color-accent)' } : { width: '100%', maxWidth: '400px', border: '2px solid var(--color-accent)' }),
                  ...props.style
                }}
                {...props}
              />
            );
          }
        }}
      >
        {enrichedContent}
      </ReactMarkdown>
      {downloadErrorGlobal && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          style={{ zIndex: 25000 }}
          role="alert"
          aria-live="assertive"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDownloadErrorGlobal(false);
          }}
        >
          <div
            className="flex items-start gap-3 p-4 rounded-md border border-red-600 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 shadow-xl max-w-sm w-full mx-4"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0" aria-hidden="true">
              <ExclamationTriangleIcon width={24} height={24} />
            </div>
            <div className="flex flex-col gap-2">
              <span className="font-semibold text-sm">Download Failed</span>
              <span className="text-xs">Sorry could not download files at this time. Try again later.</span>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  className="px-3 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                  onClick={() => setDownloadErrorGlobal(false)}
                >Close</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if content or isStreaming actually changed
  // Ignore onContentLoaded callback changes to prevent unnecessary re-renders
  return prevProps.content === nextProps.content && 
         prevProps.isStreaming === nextProps.isStreaming &&
         prevProps.className === nextProps.className &&
         prevProps.disableNormalization === nextProps.disableNormalization &&
         prevProps.translationData === nextProps.translationData &&
         prevProps.modelId === nextProps.modelId;
});

export default MessageContentRenderer;